import { queryOptions } from "@tanstack/react-query";
import { fetchApi } from "./api";
import { listNewsArticles } from "./news.functions";

export type NewsArticle = {
  id: string;
  title: string;
  slug: string;
  image_url: string | null;
  header_image_url: string | null;
  /** Alt text for the list/header image (migration 024); absent on older rows. */
  image_alt?: string | null;
  excerpt: string | null;
  external_url: string | null;
  category: string | null;
  published_at: string;
  is_active: boolean;
  has_newsletter: boolean;
  /** True when the full body is stored locally (on-site article page exists). */
  has_body: boolean;
};

export const newsQueryOptions = () =>
  queryOptions({
    queryKey: ["news_articles"],
    queryFn: async () => {
      const data = await listNewsArticles();
      return data as NewsArticle[];
    },
    // List ⇄ article round-trips must not refetch (or hit the database) every
    // time the reader comes back: the list is fresh for 5 minutes and stays
    // cached for 30, so back-navigation renders instantly from memory and the
    // /news scroll/filter restoration (see routes/news.tsx) has its full list
    // height on the first paint.
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

/** One article WITH its body, as `getNewsArticleBySlug` returns it. */
export type NewsArticleDetail = {
  id: string;
  title: string;
  slug: string;
  image_url: string | null;
  header_image_url: string | null;
  excerpt: string | null;
  body_html: string | null;
  category: string | null;
  published_at: string;
  /**
   * Migration 019. null/undefined = auto-detect (all legacy imports),
   * 'email' = force the sandboxed iframe, 'clean' = force native rendering.
   */
  body_format?: "email" | "clean" | null;
  /**
   * Migration 034. false = leave the AIA advisory board off this article;
   * missing (column not yet migrated) or true = show it.
   */
  show_advisory_board?: boolean | null;
};

/**
 * Shared by the /news/$slug page and the legacy-URL resolver (routes/$.tsx):
 * one cache key, so an article fetched while resolving an old permalink is
 * already in memory when the article page mounts.
 */
export const newsArticleQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["news_article", slug],
    queryFn: async () =>
      (await fetchApi<NewsArticleDetail | null>(
        "content",
        "getNewsArticleBySlug",
        { slug },
        { auth: false },
      )) as NewsArticleDetail | null,
  });
